import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { finalize, Observable } from 'rxjs';
import { IOutboundEmailAccount } from '../../../_interfaces/IOutboundEmailAccount';

@Component({
  selector: 'app-form-outbound-email-account',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    InputTextModule,
    SelectModule,
  ],
  templateUrl: './form-outbound-email-account.component.html',
  styleUrls: ['./form-outbound-email-account.component.scss'],
})
export class FormOutboundEmailAccountComponent implements OnInit {
  @Input({ required: true }) loadFn!: () => Observable<IOutboundEmailAccount>;
  @Input({ required: true }) saveFn!: (payload: IOutboundEmailAccount) => Observable<IOutboundEmailAccount>;
  @Input({ required: true }) testFn!: (to?: string) => Observable<any>;
  @Input() titleKey = 'ADMIN.EMAIL_ACCOUNTS.TITLE';
  @Input() descriptionKey = 'ADMIN.EMAIL_ACCOUNTS.DESCRIPTION';
  @Input() scope: 'global' | 'customer' | 'site' = 'global';

  loading = true;
  saving = false;
  testing = false;
  testDialogVisible = false;
  testDialogRecipient = '';
  testRecipient = '';
  providerFromValue = '';
  account: IOutboundEmailAccount = {
    preset: 'CUSTOM',
    enabled: true,
    secure: false,
  };

  readonly presetOptions = [
    { labelKey: 'ADMIN.EMAIL_ACCOUNTS.PRESET.CUSTOM', value: 'CUSTOM' },
    { labelKey: 'ADMIN.EMAIL_ACCOUNTS.PRESET.GMAIL', value: 'GMAIL' },
    { labelKey: 'ADMIN.EMAIL_ACCOUNTS.PRESET.OUTLOOK', value: 'OUTLOOK' },
  ];

  readonly presetDefaults: Record<
    NonNullable<IOutboundEmailAccount['preset']>,
    { host: string; port: number; secure: boolean } | null
  > = {
    CUSTOM: null,
    GMAIL: {
      host: 'smtp.gmail.com',
      port: 465,
      secure: true,
    },
    OUTLOOK: {
      host: 'smtp.office365.com',
      port: 587,
      secure: false,
    },
  };

  constructor(
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
  ) {}

  get hasHeader(): boolean {
    return !!this.titleKey || !!this.descriptionKey;
  }

  get configurationStatusKey(): string {
    return this.account.configured
      ? 'ADMIN.EMAIL_ACCOUNTS.STATUS_READY'
      : 'ADMIN.EMAIL_ACCOUNTS.STATUS_PENDING';
  }

  get configurationStatusHelpKey(): string {
    return this.account.configured
      ? 'ADMIN.EMAIL_ACCOUNTS.STATUS_READY_HELP'
      : 'ADMIN.EMAIL_ACCOUNTS.STATUS_PENDING_HELP';
  }

  get scopeNoteKey(): string | null {
    if (this.scope === 'customer') {
      return 'ADMIN.EMAIL_ACCOUNTS.SCOPE_NOTE_CUSTOMER';
    }
    if (this.scope === 'site') {
      return 'ADMIN.EMAIL_ACCOUNTS.SCOPE_NOTE_SITE';
    }
    return null;
  }

  get lastTestTone(): 'success' | 'error' | 'idle' {
    const result = `${this.account.last_test_result || ''}`.trim().toUpperCase();
    if (result.startsWith('OK:')) {
      return 'success';
    }
    if (result.startsWith('ERROR:')) {
      return 'error';
    }
    return 'idle';
  }

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading = true;
    this.loadFn()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (account) => {
          this.account = {
            preset: 'CUSTOM',
            enabled: true,
            secure: false,
            ...(account || {}),
          };
          this.applyPresetDefaults(false);
          this.syncProviderFromValue();
        },
        error: () => {
          this.account = {
            preset: 'CUSTOM',
            enabled: true,
            secure: false,
          };
          this.syncProviderFromValue();
        },
      });
  }

  save(): void {
    this.syncManagedFieldsIntoAccount();
    const payload = this.buildSavePayload();
    this.saving = true;
    this.saveFn(payload)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (account) => {
          this.account = {
            ...this.account,
            ...(account || {}),
            password: '',
          };
          this.syncProviderFromValue();
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('COMMON.SUCCESS'),
            detail: this.translate.instant('ADMIN.EMAIL_ACCOUNTS.SAVED'),
          });
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.ERROR'),
            detail: this.resolveErrorDetail(
              error,
              'ADMIN.EMAIL_ACCOUNTS.SAVE_ERROR',
            ),
          });
        },
      });
  }

  openTestDialog(): void {
    this.testDialogRecipient = `${this.testRecipient || this.account.reply_to_email || this.account.username || ''}`.trim();
    this.testDialogVisible = true;
  }

  testConnection(): void {
    const recipient = `${this.testDialogRecipient || ''}`.trim();
    if (!recipient || !this.isValidEmail(recipient)) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('COMMON.WARNING'),
        detail: this.translate.instant('ADMIN.EMAIL_ACCOUNTS.TEST_RECIPIENT'),
      });
      return;
    }

    this.testRecipient = recipient;
    this.testDialogVisible = false;
    this.testing = true;
    this.testFn(recipient)
      .pipe(finalize(() => (this.testing = false)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('COMMON.SUCCESS'),
            detail: this.translate.instant('ADMIN.EMAIL_ACCOUNTS.TEST_SUCCESS'),
          });
          this.reload();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.ERROR'),
            detail: this.resolveErrorDetail(
              error,
              'ADMIN.EMAIL_ACCOUNTS.TEST_ERROR',
            ),
          });
        },
      });
  }

  closeTestDialog(): void {
    this.testDialogVisible = false;
  }

  isManagedPreset(): boolean {
    return this.account.preset === 'GMAIL' || this.account.preset === 'OUTLOOK';
  }

  onPresetChange(): void {
    this.applyPresetDefaults(true);
    this.syncProviderFromValue();
  }

  private applyPresetDefaults(syncFromEmail: boolean): void {
    const preset = this.account.preset || 'CUSTOM';
    const defaults = this.presetDefaults[preset];
    if (!defaults) {
      return;
    }

    this.account.host = defaults.host;
    this.account.port = defaults.port;
    this.account.secure = defaults.secure;

    if (syncFromEmail && this.account.username && !this.account.from_email) {
      this.account.from_email = this.account.username;
    }
  }

  private syncProviderFromValue(): void {
    const fromName = `${this.account.from_name || ''}`.trim();
    const fromEmail = `${this.account.from_email || ''}`.trim();

    if (fromName && fromEmail) {
      this.providerFromValue = `${fromName} <${fromEmail}>`;
      return;
    }

    this.providerFromValue = fromEmail || fromName || '';
  }

  private syncManagedFieldsIntoAccount(): void {
    if (!this.isManagedPreset()) {
      return;
    }

    const username = `${this.account.username || ''}`.trim();
    if (username && !this.account.from_email) {
      this.account.from_email = username;
    }

    const rawFrom = `${this.providerFromValue || ''}`.trim();
    if (!rawFrom) {
      this.account.from_name = null;
      this.account.from_email = username || null;
      return;
    }

    const match = rawFrom.match(/^(.*)<\s*([^<>]+)\s*>$/);
    if (match) {
      const name = match[1].trim();
      const email = match[2].trim();
      this.account.from_name = name || null;
      this.account.from_email = email || null;
      return;
    }

    if (rawFrom.includes('@')) {
      this.account.from_name = null;
      this.account.from_email = rawFrom;
      return;
    }

    this.account.from_name = rawFrom;
    this.account.from_email = username || this.account.from_email || null;
  }

  private buildSavePayload(): IOutboundEmailAccount {
    return {
      enabled: !!this.account.enabled,
      preset: this.account.preset || 'CUSTOM',
      host: this.account.host || null,
      port:
        this.account.port === undefined || this.account.port === null || this.account.port === ('' as any)
          ? null
          : +this.account.port,
      secure: !!this.account.secure,
      username: this.account.username || null,
      password: this.account.password || undefined,
      from_name: this.account.from_name || null,
      from_email: this.account.from_email || null,
      reply_to_email: this.account.reply_to_email || null,
    };
  }

  private resolveErrorDetail(error: any, fallbackKey: string): string {
    const backendMessages = Array.isArray(error?.error?.message)
      ? error.error.message
      : [error?.error?.message || error?.message].filter(Boolean);

    const detail = backendMessages
      .map((message) => this.translateBackendCode(String(message)))
      .filter(Boolean)
      .join('\n');

    return detail || this.translate.instant(fallbackKey);
  }

  private translateBackendCode(code: string): string {
    const normalized = `${code || ''}`.trim();
    if (!normalized) return '';

    const translated = this.translate.instant(normalized);
    if (translated !== normalized) {
      return translated;
    }

    return normalized;
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }
}
